import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Play } from "lucide-react";

const DEMO_EMAIL = "user@klfn210.com";
const DEMO_PASSWORD = "user#2026";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      if (u.status !== "approved") nav("/pending");
      else nav("/portal");
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };

  const enterDemo = async () => {
    setErr(""); setDemoLoading(true);
    try {
      const u = await login(DEMO_EMAIL, DEMO_PASSWORD);
      if (u.status !== "approved") nav("/pending");
      else nav("/portal");
    } catch (e) {
      setErr(`Demo login failed: ${e.message}. Please contact the portal owner — the demo account should be seeded automatically on every deploy.`);
    } finally { setDemoLoading(false); }
  };

  return (
    <div className="min-h-[85vh] px-6 py-16 flex items-center justify-center">
      <div className="w-full max-w-md corner-tick border border-white/12 bg-[color:var(--surface)] p-8 relative">
        <div className="absolute -top-3 left-6 bg-[color:var(--bg)] px-3 label-mono">Portal access</div>
        <h1 className="font-display text-3xl mt-2">Sign in</h1>
        <p className="text-white/55 text-sm mt-2">Verified band members only. The registry clerk approves every account.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="label-mono block mb-2">Email</label>
            <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="w-full px-3 py-2.5 text-sm" data-testid="login-email-input" />
          </div>
          <div>
            <label className="label-mono block mb-2">Password</label>
            <input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="w-full px-3 py-2.5 text-sm" data-testid="login-password-input" />
          </div>
          {err && <div className="border border-[color:var(--signal)]/50 bg-[color:var(--signal)]/10 text-[color:var(--signal)] text-sm p-3" data-testid="login-error">{err}</div>}
          <button type="submit" disabled={loading || demoLoading} className="btn-cta primary w-full justify-center" data-testid="login-submit-btn">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        {/* One-click stakeholder demo — no typing, no risk of mistyping the # in the password. */}
        <div className="mt-6 border border-[#d4b876]/40 bg-[#d4b876]/5 p-4" data-testid="demo-tour-panel">
          <div className="label-mono mb-2" style={{ color: "#d4b876" }}>::: LIVE DEMO TOUR</div>
          <p className="text-white/70 text-xs leading-relaxed mb-3">
            Touring the portal for the first time? Try the full-fidelity demo — you'll enter as a Chief with every module unlocked.
            Destructive actions (delete, approve applicants) are safely disabled.
          </p>
          <button
            type="button"
            onClick={enterDemo}
            disabled={loading || demoLoading}
            className="btn-cta w-full justify-center"
            style={{ borderColor: "#d4b876", color: "#d4b876" }}
            data-testid="demo-tour-btn"
          >
            <Play size={12}/> {demoLoading ? "Entering demo…" : "Enter the demo tour"}
          </button>
          <div className="font-mono text-[10px] text-white/40 mt-3">
            or sign in manually with <span className="text-white/70">{DEMO_EMAIL}</span> / <span className="text-white/70">{DEMO_PASSWORD}</span>
          </div>
        </div>

        <div className="mt-6 border-t border-white/8 pt-4 font-mono text-xs text-white/50">
          No account? <Link to="/register" className="text-[color:var(--cobalt)] hover:underline" data-testid="login-to-register">Apply through band registry →</Link>
        </div>
      </div>
    </div>
  );
}
